import { auth, getAppCheckTokenSafe } from '../firebase/config';

const isLocal = window.location.hostname === 'localhost';

export const FUNCTION_BASE = isLocal
  ? 'http://localhost:5001/askpastorrob-b6812/us-central1'
  : 'https://us-central1-askpastorrob-b6812.cloudfunctions.net';

/** Where the public app lives — used in share text and cross-app links. */
export const PUBLIC_SITE_URL = 'https://askpastorrob.com';
export const ADMIN_SITE_URL = 'https://admin.askpastorrob.com';

/**
 * POSTs to a Cloud Function with the caller's Firebase ID token (and App
 * Check token when available). All chat endpoints require this — the
 * backend derives identity from the token, never from the body.
 *
 * `signal` (AbortSignal) lets the caller abandon a streaming answer.
 */
export async function authedFetch(functionName, body, { signal } = {}) {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }

  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${await user.getIdToken()}`,
  };

  const appCheckToken = await getAppCheckTokenSafe();
  if (appCheckToken) {
    headers['X-Firebase-AppCheck'] = appCheckToken;
  }

  return fetch(`${FUNCTION_BASE}/${functionName}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
    signal,
  });
}

// Every card a refusal can carry (functions/guards.js CARD_CATEGORIES, and a
// direct threat's): a medical emergency's card is 911 first with Poison
// Control (round 2 review); a threat's is 911 first too.
const CARD_CATEGORIES = ['self_harm', 'abuse', 'medical', 'harm_others'];

/**
 * A question-window refusal says when the window ends, what it allowed, and
 * whether an email or an account would give more; under a safety hold it
 * says givingQuiet, so its card carries the 988 line and no ask
 * (utils/giving.js). A refusal of a crisis message carries its card
 * (functions/chat.js), whatever turned it away.
 */
function withWindow(err, payload) {
  if (Number.isFinite(payload?.resetAt)) err.resetAt = payload.resetAt;
  if (Number.isFinite(payload?.limit)) err.limit = payload.limit;
  if (typeof payload?.moreWithEmail === 'boolean') err.moreWithEmail = payload.moreWithEmail;
  if (typeof payload?.moreWithAccount === 'boolean') err.moreWithAccount = payload.moreWithAccount;
  if (payload?.givingQuiet === true) err.givingQuiet = true;
  if (CARD_CATEGORIES.includes(payload?.safety?.category)) err.safetyCategory = payload.safety.category;
  return err;
}

/**
 * Turns a non-OK response into an Error carrying the server's sanitized
 * message (rate limits, daily capacity, etc.) so the UI can show it verbatim.
 */
export async function errorFromResponse(response) {
  let payload = null;
  try {
    payload = await response.json();
  } catch (e) {
    // Non-JSON error body; fall through to generic error
  }
  const err = new Error(payload?.error || 'Network response was not ok');
  err.status = response.status;
  if (payload?.error) {
    err.safeMessage = payload.error;
    err.code = payload.code;
  }
  return withWindow(err, payload);
}

/** Wraps an SSE error payload the same way (server strings are pre-sanitized). */
export function errorFromSse(dataObj) {
  const err = new Error(dataObj.error);
  err.safeMessage = dataObj.error;
  err.code = dataObj.code;
  return withWindow(err, dataObj);
}

/**
 * The client mints the conversation id: it names the conversation the team
 * keeps (functions/conversations.js) and, if the person saves it, their chat.
 */
export function newChatId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'chat-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
}

// ---------------------------------------------------------------------------
// Periodic AI re-disclosure (session start notice lives in the disclosure bar;
// this adds an in-conversation reminder every 3 hours of continued use).
// ---------------------------------------------------------------------------

const RENOTICE_KEY = 'apr_ai_notice_at';
const RENOTICE_INTERVAL_MS = 3 * 60 * 60 * 1000;

export function aiRenoticeDue() {
  try {
    const last = parseInt(sessionStorage.getItem(RENOTICE_KEY), 10);
    const now = Date.now();
    if (!Number.isFinite(last)) {
      sessionStorage.setItem(RENOTICE_KEY, String(now));
      return false;
    }
    if (now - last >= RENOTICE_INTERVAL_MS) {
      sessionStorage.setItem(RENOTICE_KEY, String(now));
      return true;
    }
  } catch (e) {
    // sessionStorage unavailable — skip re-notices
  }
  return false;
}

// The crisis link sits in the disclosure bar, above the conversation
// (BRAND_SPEC §11.1 fixed "below"). Shown as a plain note, not an AI turn.
export const AI_RENOTICE_TEXT = 'A gentle reminder: you\'re talking with an AI that answers from Pastor Rob\'s sermons — not Pastor Rob himself, and not a counselor. For anything urgent, real people are ready to help — see the crisis link above.';
